import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Cpu, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { MaskedLines } from "@/components/motion";

const PILLARS = [
  {
    n: "01",
    slug: "endur-consulting",
    icon: Cpu,
    title: "ETRM & Openlink Endur Consulting",
    body: "Deep ETRM and Openlink Endur expertise across business analysis, functional design, implementation, testing and delivery. We work alongside energy and commodity trading organisations to help them modernise, upgrade and optimise their Endur estate with the discipline of senior practitioners.",
    bullets: [
      "Endur strategy, assessment and roadmap",
      "Functional design and business analysis",
      "Implementation, upgrade and migration",
      "Testing, cutover and hyper-care",
    ],
  },
  {
    n: "02",
    slug: "digital-transformation",
    icon: Layers,
    title: "Trading Technology & Transformation",
    body: "Helping energy and commodity businesses modernise trading platforms, processes, data and technology. We combine trading domain fluency with modern cloud, data and AI foundations to deliver durable outcomes for the trading enterprise.",
    bullets: [
      "Trading platform strategy and architecture",
      "System integration and data foundations",
      "Cloud modernisation and analytics",
      "AI-enabled trading capability",
    ],
  },
];

export default function Services() {
  useSEO({
    title: "Services — ETRM & Openlink Endur · Trading Technology & Transformation | EnPrex",
    description: "EnPrex Services delivers specialist ETRM and Openlink Endur consulting, and trading technology & transformation for energy and commodity trading organisations.",
    path: "/services",
  });
  return (
    <div data-testid="services-page">
      <section className="container-x pt-16 pb-16 border-b border-white/5">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— Services</div>
          <h1 className="font-display text-5xl md:text-7xl font-light text-white tracking-tighter leading-[0.98] max-w-5xl">
            <MaskedLines delay={0.15} lines={[<>Two focused practices.</>, <><span className="italic">One</span> specialist firm.</>]} />
          </h1>
          <p className="mt-8 text-lg text-slate-300 max-w-3xl font-light leading-relaxed">
            EnPrex delivers specialist consulting in energy and commodity trading technology. Two pillars — deep and integrated.
          </p>
        </motion.div>
      </section>

      <section className="container-x py-16 space-y-12">
        {PILLARS.map((p) => {
          const Icon = p.icon;
          return (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="border border-white/5 bg-white/[0.02] p-10 md:p-14 grid lg:grid-cols-12 gap-10"
              data-testid={`service-pillar-${p.slug}`}
            >
              <div className="lg:col-span-4">
                <div className="font-display text-6xl md:text-7xl font-light text-[#D4AF37]">{p.n}</div>
                <Icon className="text-white/70 mt-8" size={32} strokeWidth={1.2} />
              </div>
              <div className="lg:col-span-8">
                <h2 className="font-display text-3xl md:text-4xl text-white font-light tracking-tight leading-tight">
                  {p.title}
                </h2>
                <p className="mt-6 text-lg text-slate-300 font-light leading-relaxed">
                  {p.body}
                </p>
                <ul className="mt-8 grid md:grid-cols-2 gap-3">
                  {p.bullets.map((b) => (
                    <li key={b} className="text-sm text-slate-400 leading-relaxed">— {b}</li>
                  ))}
                </ul>
                <Link
                  to={`/services/${p.slug}`}
                  data-testid={`service-pillar-link-${p.slug}`}
                  className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#D4AF37] border-b border-[#D4AF37] pb-1 hover:text-[#FDE047] transition-colors"
                >
                  Explore this practice <ArrowUpRight size={14} />
                </Link>
              </div>
            </motion.article>
          );
        })}
      </section>

      <section className="container-x pb-24">
        <div className="border border-[#D4AF37]/25 bg-gradient-to-br from-[#0B1F3A] to-[#050A14] p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-white font-light tracking-tight max-w-2xl">
              Discuss a project or a specific challenge with our team.
            </h2>
          </div>
          <Link to="/contact" data-testid="services-cta-contact" className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#FDE047] transition-colors whitespace-nowrap">
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
